import { useEffect, useRef, useState, type ReactNode } from "react";
import { Link, useFetcher, useLocation } from "react-router";
import type {
  CommentPage,
  ReactionSummary,
  ReactorPage,
  SpaceComment,
  UserList,
  UserSummary,
} from "@repo/shared-types";
import { SmilePlus } from "lucide-react";
import { newId } from "@/lib/ids";
import { timeAgo } from "@/lib/inbox";
import { REACTIONS, insertMention, mentionAt, reactionEmoji, splitBody } from "@/lib/mentions";
import { cn } from "@/lib/utils";
import type { ConversationResult } from "@/routes/conversation";
import { Avatar } from "@/components/avatar";
import { ErrorNote } from "@/components/form";
import { Modal } from "@/components/modal";
import { usePop } from "@/components/pop";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { InputGroup, InputGroupAddon, InputGroupButton, InputGroupTextarea } from "@/components/ui/input-group";
import { Item, ItemContent, ItemDescription, ItemMedia, ItemTitle } from "@/components/ui/item";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Spinner } from "@/components/ui/spinner";

/**
 * A conversation (7e-2): the reactions to an event or a shared result, and its
 * thread — top-level comments oldest first, each with its first replies.
 *
 * The first page is the page's query (0075); everything past it is read where
 * it is shown, through the space's conversation route, and every write goes
 * there too, after which the page reads the thread again. What was read past
 * the first page, or written here, is kept on the page while it is open, each
 * comment once.
 */

export type ThreadTarget = {
  /** The space's slug, or "me" for the log's read of your own result. */
  slug: string;
  eventId: string;
  /** A result's thread; null for the event's own. */
  entryId: string | null;
  /** A team's thread (0027), in place of a result's; left out for none. */
  teamId?: string | null;
};

export function conversationPath(slug: string, query?: Record<string, string | null | undefined>): string {
  const path = `/t/${slug}/conversation`;
  if (!query) return path;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) if (value) params.set(key, value);
  return `${path}?${params}`;
}

/** A fresh id for a comment about to be written, so a retry never writes it twice. */
function newCommentId(): string {
  return newId();
}

/** Keeps the first of each comment, in order. */
function unique(comments: SpaceComment[]): SpaceComment[] {
  const seen = new Set<string>();
  return comments.filter((comment) => (seen.has(comment.id) ? false : (seen.add(comment.id), true)));
}

export function Conversation({
  target,
  page,
  reactions,
  writes,
  closed,
  replyIn,
  title = "Conversation",
}: {
  target: ThreadTarget;
  page: CommentPage;
  /** How people reacted to what the thread is on; null where that is not read. */
  reactions: ReactionSummary | null;
  /** False from the log, which reads the thread and writes nothing. */
  writes: boolean;
  /** Why nothing can be written yet — an event not published — or null. */
  closed?: string | null;
  /** From the log: where to reply instead, in the result's space. */
  replyIn?: { to: string; name: string } | null;
  /** Its heading, and its name to a screen reader: the log names a team's thread by its part. */
  title?: string;
}) {
  const more = useFetcher<CommentPage>();
  const [extra, setExtra] = useState<SpaceComment[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [posted, setPosted] = useState<SpaceComment[]>([]);
  const now = Date.now();
  const { hash } = useLocation();

  // An inbox line leads to a comment by its id in the address: scroll to it.
  // A reply past the first few is not on the page until asked for.
  useEffect(() => {
    if (!hash.startsWith("#comment-")) return;
    document.getElementById(hash.slice(1))?.scrollIntoView?.({ block: "center" });
  }, [hash]);

  useEffect(() => {
    if (!more.data || !("comments" in more.data)) return;
    const loaded = more.data;
    setExtra((shown) => [...shown, ...loaded.comments]);
    setCursor(loaded.nextCursor ?? "");
  }, [more.data]);

  // Until a later page is read, the first page says whether there is one.
  const next = cursor === null ? page.nextCursor : cursor || null;
  const comments = unique([...page.comments, ...extra, ...posted]);
  // Whether reacting and replying are offered: never from the log, nor
  // before the event is published.
  const open = writes && !closed;

  return (
    <section className="space-y-3" aria-label={title}>
      <h2 className="section-label">{title}</h2>
      {/* Not clipped, so the members typing @ suggests may hang past its edge. */}
      <Card className="overflow-visible">
        <CardContent className="space-y-6">
          {reactions && <ReactionBar target={target} commentId={null} summary={reactions} writes={open} />}

          {comments.length === 0 ? (
            <p className="text-sm text-muted-foreground">No comments yet.</p>
          ) : (
            <ol className="space-y-6">
              {comments.map((comment) => (
                <li key={comment.id}>
                  <CommentItem target={target} comment={comment} writes={open} now={now} />
                </li>
              ))}
            </ol>
          )}

          {next && (
            <Button
              type="button"
              variant="outline"
              className="w-full"
              disabled={more.state !== "idle"}
              onClick={() =>
                more.load(
                  conversationPath(target.slug, {
                    want: "comments",
                    event: target.eventId,
                    entry: target.entryId,
                    team: target.teamId,
                    cursor: next,
                  }),
                )
              }
            >
              Show more comments
            </Button>
          )}

          {writes ? (
            closed ? (
              <p className="text-sm text-muted-foreground">{closed}</p>
            ) : (
              <Composer
                target={target}
                replyTo={null}
                label="Write a comment"
                onPosted={(comment) => setPosted((shown) => [...shown, comment])}
              />
            )
          ) : (
            replyIn && (
              <p className="text-sm">
                <Link to={replyIn.to} className="font-medium underline underline-offset-4">
                  Reply in {replyIn.name}
                </Link>
              </p>
            )
          )}
        </CardContent>
      </Card>
    </section>
  );
}

/**
 * One comment: who, when, what it says, how people reacted, and — on a
 * top-level one — its replies, the first few and the rest on asking. Its
 * author edits it; its author, the owner and moderators delete it.
 */
function CommentItem({
  target,
  comment: loaded,
  writes,
  now,
  onReply,
}: {
  target: ThreadTarget;
  comment: SpaceComment;
  writes: boolean;
  now: number;
  /** On a reply: answering it opens its top-level comment's reply box. */
  onReply?: (to: UserSummary) => void;
}) {
  const [comment, setComment] = useState(loaded);
  const [editing, setEditing] = useState(false);
  const [replying, setReplying] = useState<string | null>(null);
  const [replies, setReplies] = useState<SpaceComment[]>([]);
  const [repliesCursor, setRepliesCursor] = useState<string | null>(null);
  const removal = useFetcher<ConversationResult>();
  const [deleting, setDeleting] = useState(false);
  const moreReplies = useFetcher<CommentPage>();
  const linked = useLocation().hash === `#comment-${loaded.id}`;

  // The page read the thread again: that is the comment now.
  useEffect(() => setComment(loaded), [loaded]);
  useEffect(() => {
    if (!moreReplies.data || !("comments" in moreReplies.data)) return;
    const loadedPage = moreReplies.data;
    setReplies((shown) => [...shown, ...loadedPage.comments]);
    setRepliesCursor(loadedPage.nextCursor ?? "");
  }, [moreReplies.data]);

  if (removal.data?.deleted === comment.id) return null;

  const topLevel = comment.parentId === null;
  const shownReplies = unique([...comment.replies, ...replies]);
  const nextReplies = repliesCursor === null ? comment.repliesCursor : repliesCursor || null;
  const unshown = comment.replyCount - shownReplies.length;
  const answer = (to: UserSummary) => setReplying(`@${to.username} `);

  return (
    <article
      id={`comment-${comment.id}`}
      data-linked={linked || undefined}
      className={cn("flex gap-3", linked && "-mx-2 rounded-2xl bg-warning-muted px-2 py-2")}
    >
      <Avatar person={comment.author} size={topLevel ? "md" : "sm"} className="mt-0.5 shrink-0" />
      <div className="min-w-0 flex-1 space-y-1.5">
        <p className="text-sm text-muted-foreground">
          <span className="font-medium text-foreground">@{comment.author.username}</span> ·{" "}
          {timeAgo(comment.createdAt, now)}
          {comment.editedAt && " · edited"}
        </p>
        {editing ? (
          <EditForm
            target={target}
            comment={comment}
            onDone={(edited) => {
              // What an edit answers carries no replies: they are as they were.
              if (edited) {
                setComment({
                  ...edited,
                  replies: comment.replies,
                  replyCount: comment.replyCount,
                  repliesCursor: comment.repliesCursor,
                });
              }
              setEditing(false);
            }}
          />
        ) : (
          <p className="text-sm whitespace-pre-line text-foreground wrap-anywhere">
            <CommentText comment={comment} />
          </p>
        )}
        <div className="-ml-1 flex flex-wrap items-center gap-x-1 gap-y-2">
          <ReactionBar target={target} commentId={comment.id} summary={comment.reactions} writes={writes} />
          {writes && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="text-muted-foreground"
              onClick={() => (topLevel ? setReplying("") : onReply?.(comment.author))}
            >
              Reply
            </Button>
          )}
          {comment.canEdit && !editing && (
            <Button type="button" variant="ghost" size="sm" className="text-muted-foreground" onClick={() => setEditing(true)}>
              Edit
            </Button>
          )}
          {comment.canDelete && (
            <Button type="button" variant="ghost" size="sm" className="text-muted-foreground" onClick={() => setDeleting(true)}>
              Delete
            </Button>
          )}
        </div>
        {/* Asked first, in the sheet every deletion uses, drawn as
            ConfirmDialog draws it. Posted to the conversation's route rather
            than the page's, which ConfirmDialog cannot, so it is drawn here;
            once it answers, the comment is gone and the sheet with it. */}
        {deleting && (
          <Modal
            title={topLevel && comment.replyCount > 0 ? "Delete it and its replies?" : "Delete this comment?"}
            onClose={() => setDeleting(false)}
          >
            <removal.Form method="post" action={conversationPath(target.slug)} className="space-y-4">
              <input type="hidden" name="intent" value="delete" />
              <input type="hidden" name="comment" value={comment.id} />
              <p className="text-sm text-muted-foreground">
                It goes from the conversation for everyone. It cannot be undone.
              </p>
              {removal.data?.error && removal.state === "idle" && <ErrorNote message={removal.data.error} />}
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" size="lg" onClick={() => setDeleting(false)}>
                  Cancel
                </Button>
                <Button type="submit" variant="destructive" size="lg" disabled={removal.state !== "idle"}>
                  Delete comment
                </Button>
              </div>
            </removal.Form>
          </Modal>
        )}

        {topLevel && (shownReplies.length > 0 || replying !== null) && (
          <div className="space-y-4 pt-3">
            {shownReplies.map((reply) => (
              <CommentItem key={reply.id} target={target} comment={reply} writes={writes} now={now} onReply={answer} />
            ))}
            {nextReplies && unshown > 0 && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="-ml-3 text-muted-foreground"
                disabled={moreReplies.state !== "idle"}
                onClick={() =>
                  moreReplies.load(
                    conversationPath(target.slug, {
                      want: "replies",
                      comment: comment.id,
                      entry: target.slug === "me" ? target.entryId : null,
                      team: target.slug === "me" ? target.teamId : null,
                      cursor: nextReplies,
                    }),
                  )
                }
              >
                Show {unshown} more {unshown === 1 ? "reply" : "replies"}
              </Button>
            )}
            {replying !== null && (
              <Composer
                key={replying}
                target={target}
                replyTo={comment.id}
                label={`Reply to @${comment.author.username}`}
                initial={replying}
                autoFocus
                onPosted={(reply) => {
                  setReplies((shown) => [...shown, reply]);
                  setReplying(null);
                }}
                onCancel={() => setReplying(null)}
              />
            )}
          </div>
        )}
      </div>
    </article>
  );
}

/** What a comment says, each mention as @ and the username it has now. */
function CommentText({ comment }: { comment: SpaceComment }) {
  return (
    <>
      {splitBody(comment.body, comment.mentions).map((part, i) =>
        "text" in part ? (
          <span key={i}>{part.text}</span>
        ) : part.mention.username ? (
          <span key={i} className="font-medium text-foreground">
            @{part.mention.username}
          </span>
        ) : (
          <span key={i} className="text-muted-foreground italic">
            deleted account
          </span>
        ),
      )}
    </>
  );
}

/**
 * A box to write in, which suggests the space's members as somebody types
 * @. Sent with an id of its own, so a retry never writes it twice.
 */
function Composer({
  target,
  replyTo,
  label,
  initial = "",
  autoFocus = false,
  onPosted,
  onCancel,
}: {
  target: ThreadTarget;
  replyTo: string | null;
  label: string;
  initial?: string;
  autoFocus?: boolean;
  onPosted: (comment: SpaceComment) => void;
  onCancel?: () => void;
}) {
  const fetcher = useFetcher<ConversationResult>();
  const [text, setText] = useState(initial);
  const [id, setId] = useState(newCommentId);
  const handled = useRef<ConversationResult | undefined>(undefined);

  useEffect(() => {
    const result = fetcher.data;
    if (fetcher.state !== "idle" || !result?.comment || handled.current === result) return;
    handled.current = result;
    onPosted(result.comment);
    setText("");
    setId(newCommentId());
  }, [fetcher.state, fetcher.data, onPosted]);

  return (
    <fetcher.Form method="post" action={conversationPath(target.slug)} className="space-y-2">
      <input type="hidden" name="intent" value="comment" />
      <input type="hidden" name="id" value={id} />
      <input type="hidden" name="event" value={target.eventId} />
      {target.entryId && <input type="hidden" name="entry" value={target.entryId} />}
      {target.teamId && <input type="hidden" name="team" value={target.teamId} />}
      {replyTo && <input type="hidden" name="replyTo" value={replyTo} />}
      <MentionBox slug={target.slug} label={label} value={text} onChange={setText} autoFocus={autoFocus}>
        {onCancel && (
          <InputGroupButton size="sm" variant="ghost" onClick={onCancel}>
            Cancel
          </InputGroupButton>
        )}
        <InputGroupButton type="submit" size="sm" variant="default" disabled={fetcher.state !== "idle" || text.trim() === ""}>
          {replyTo ? "Reply" : "Post"}
        </InputGroupButton>
      </MentionBox>
      {fetcher.data?.error && fetcher.state === "idle" && <p className="text-sm text-destructive">{fetcher.data.error}</p>}
    </fetcher.Form>
  );
}

function EditForm({
  target,
  comment,
  onDone,
}: {
  target: ThreadTarget;
  comment: SpaceComment;
  onDone: (edited: SpaceComment | null) => void;
}) {
  const fetcher = useFetcher<ConversationResult>();
  const [text, setText] = useState(comment.text);

  useEffect(() => {
    if (fetcher.state === "idle" && fetcher.data?.comment) onDone(fetcher.data.comment);
  }, [fetcher.state, fetcher.data, onDone]);

  return (
    <fetcher.Form method="post" action={conversationPath(target.slug)} className="space-y-2">
      <input type="hidden" name="intent" value="edit" />
      <input type="hidden" name="comment" value={comment.id} />
      <MentionBox slug={target.slug} label="Edit your comment" value={text} onChange={setText} autoFocus>
        <InputGroupButton size="sm" variant="ghost" onClick={() => onDone(null)}>
          Cancel
        </InputGroupButton>
        <InputGroupButton type="submit" size="sm" variant="default" disabled={fetcher.state !== "idle"}>
          Save
        </InputGroupButton>
      </MentionBox>
      {fetcher.data?.error && <p className="text-sm text-destructive">{fetcher.data.error}</p>}
    </fetcher.Form>
  );
}

/**
 * The text box, with the space's members suggested while an @name is being
 * typed — above the box, so a bar fixed along the bottom of a phone's screen
 * never covers them. Picking one writes the whole username; the api
 * resolves it either way. `children` are the box's buttons, along its foot.
 */
function MentionBox({
  slug,
  label,
  value,
  onChange,
  autoFocus,
  children,
}: {
  slug: string;
  label: string;
  value: string;
  onChange: (text: string) => void;
  autoFocus?: boolean;
  children: ReactNode;
}) {
  const suggestions = useFetcher<UserList>();
  const box = useRef<HTMLTextAreaElement>(null);
  const [typing, setTyping] = useState<{ start: number; query: string } | null>(null);
  const asked = useRef<string | null>(null);

  const follow = (text: string, caret: number) => {
    const at = mentionAt(text, caret);
    setTyping(at);
    if (at && asked.current !== at.query) {
      asked.current = at.query;
      suggestions.load(conversationPath(slug, { want: "members", q: at.query || null }));
    }
  };
  const users = typing && suggestions.data && "users" in suggestions.data ? suggestions.data.users : [];

  return (
    <div className="relative">
      <InputGroup>
        <InputGroupTextarea
          ref={box}
          name="text"
          rows={2}
          maxLength={2000}
          value={value}
          aria-label={label}
          placeholder={`${label}… (@ to mention)`}
          autoFocus={autoFocus}
          onChange={(event) => {
            onChange(event.target.value);
            follow(event.target.value, event.target.selectionStart);
          }}
          onBlur={() => window.setTimeout(() => setTyping(null), 150)}
        />
        <InputGroupAddon align="block-end" className="justify-end gap-1">
          {children}
        </InputGroupAddon>
      </InputGroup>
      {users.length > 0 && typing && (
        <ul
          aria-label="Members to mention"
          className="absolute bottom-full left-0 z-20 mb-2 w-64 max-w-full rounded-2xl bg-popover p-1 text-popover-foreground shadow-lg ring-1 ring-foreground/5 dark:ring-foreground/10"
        >
          {users.map((user) => (
            <li key={user.id}>
              <Button
                type="button"
                variant="ghost"
                className="w-full justify-start rounded-xl font-normal"
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => {
                  const inserted = insertMention(value, typing, user.username);
                  onChange(inserted.text);
                  setTyping(null);
                  asked.current = null;
                  window.requestAnimationFrame(() => {
                    box.current?.focus();
                    box.current?.setSelectionRange(inserted.caret, inserted.caret);
                  });
                }}
              >
                <Avatar person={user} size="xs" />
                <span className="truncate">@{user.username}</span>
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * How people reacted: each reaction given, with its count, the caller's own
 * highlighted; tapping a count lists who, a page at a time, in a sheet. The
 * + offers the six: picking one gives it or changes to it, picking your own
 * takes it back.
 */
export function ReactionBar({
  target,
  commentId,
  summary: loaded,
  writes,
}: {
  target: ThreadTarget;
  commentId: string | null;
  summary: ReactionSummary;
  writes: boolean;
}) {
  const fetcher = useFetcher<ConversationResult>();
  const [listing, setListing] = useState<string | null>(null);
  const [picking, setPicking] = useState(false);
  const pop = usePop();

  // What the caller's own change answered, until the page reads it again.
  const summary = (fetcher.state === "idle" && fetcher.data?.reactions) || loaded;
  if (!writes && summary.counts.length === 0) return null;
  return (
    <div className="space-y-1">
      <div className="flex flex-wrap items-center gap-1.5" aria-label="Reactions">
        {summary.counts.map((count) =>
          writes ? (
            <Button
              key={count.name}
              type="button"
              variant={summary.mine === count.name ? "secondary" : "outline"}
              size="sm"
              aria-pressed={summary.mine === count.name}
              aria-label={`${count.count} reacted ${count.name}: see who`}
              className="gap-1.5 px-2.5 tabular-nums aria-pressed:border-foreground/25"
              onClick={() => setListing(count.name)}
            >
              {pop.wrap(count.name, <span aria-hidden="true">{reactionEmoji(count.name)}</span>)}
              {count.count}
            </Button>
          ) : (
            <Badge key={count.name} variant="outline" className="h-7 px-2.5 text-sm font-normal tabular-nums">
              {reactionEmoji(count.name)} {count.count}
            </Badge>
          ),
        )}
        {writes && (
          <Popover open={picking} onOpenChange={setPicking}>
            <PopoverTrigger asChild>
              <Button type="button" variant="ghost" size="icon-sm" aria-label="React" className="text-muted-foreground">
                <SmilePlus />
              </Button>
            </PopoverTrigger>
            <PopoverContent
              align="start"
              collisionPadding={16}
              aria-label="Pick a reaction"
              className="w-auto flex-row gap-0.5 rounded-full p-1"
            >
              {REACTIONS.map((reaction) => {
                const mine = summary.mine === reaction.name;
                return (
                  <Button
                    key={reaction.name}
                    type="button"
                    variant={mine ? "secondary" : "ghost"}
                    size="icon"
                    aria-label={mine ? `Take back ${reaction.label}` : reaction.label}
                    aria-pressed={mine}
                    disabled={fetcher.state !== "idle"}
                    className="rounded-full text-lg"
                    onClick={() => {
                      setPicking(false);
                      if (!mine) pop.play(reaction.name);
                      // Your own, picked again, is taken back.
                      void fetcher.submit(
                        {
                          intent: mine ? "unreact" : "react",
                          name: reaction.name,
                          event: target.eventId,
                          entry: target.entryId ?? "",
                          team: target.teamId ?? "",
                          comment: commentId ?? "",
                        },
                        { method: "post", action: conversationPath(target.slug) },
                      );
                    }}
                  >
                    {reaction.emoji}
                  </Button>
                );
              })}
            </PopoverContent>
          </Popover>
        )}
      </div>
      {fetcher.data?.error && fetcher.state === "idle" && <p className="text-sm text-destructive">{fetcher.data.error}</p>}
      {listing && (
        <Reactors target={target} commentId={commentId} name={listing} onClose={() => setListing(null)} />
      )}
    </div>
  );
}

/** Who reacted one way, in a sheet: a page at a time, oldest first. */
function Reactors({
  target,
  commentId,
  name,
  onClose,
}: {
  target: ThreadTarget;
  commentId: string | null;
  name: string;
  onClose: () => void;
}) {
  const who = useFetcher<ReactorPage>();
  const [people, setPeople] = useState<ReactorPage["reactors"]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const read = (from: string | null) =>
    who.load(
      conversationPath(target.slug, {
        want: "reactors",
        event: target.eventId,
        entry: target.entryId,
        team: target.teamId,
        comment: commentId,
        name,
        cursor: from,
      }),
    );

  // The first page once, as the sheet opens; the rest on asking.
  useEffect(() => {
    void read(null);
  }, []);
  useEffect(() => {
    if (!who.data || !("reactors" in who.data)) return;
    const page = who.data;
    setPeople((shown) => [...shown, ...page.reactors]);
    setCursor(page.nextCursor);
  }, [who.data]);

  return (
    <Modal title={`Who reacted ${reactionEmoji(name)}`} onClose={onClose}>
      <div className="space-y-3">
        {people.length === 0 && who.state !== "idle" ? (
          <Spinner className="mx-auto size-5 text-muted-foreground" />
        ) : (
          <ul className="-mx-3 space-y-0.5">
            {people.map((reactor) => (
              <li key={reactor.user.id}>
                <Item size="xs">
                  <ItemMedia>
                    <Avatar person={reactor.user} size="md" />
                  </ItemMedia>
                  <ItemContent className="min-w-0 gap-0">
                    {reactor.user.name && <ItemTitle className="block w-full truncate">{reactor.user.name}</ItemTitle>}
                    <ItemDescription className={cn("truncate", !reactor.user.name && "text-foreground")}>
                      @{reactor.user.username}
                    </ItemDescription>
                  </ItemContent>
                </Item>
              </li>
            ))}
          </ul>
        )}
        {cursor && (
          <Button type="button" variant="outline" className="w-full" disabled={who.state !== "idle"} onClick={() => read(cursor)}>
            More →
          </Button>
        )}
      </div>
      <Button type="button" size="lg" className="w-full" onClick={onClose}>
        Done
      </Button>
    </Modal>
  );
}
